import React, { useMemo, useState } from 'react';
import {
  Title, Button, Icon, Text, Spacer, Seperator
} from 'components/atoms';
import styled, { css } from 'styled-components/macro';
import { useRouterContext } from 'hooks';
import { useEditorContext, actions } from 'context/EditorContext';
import modules, { moduleDefaults } from 'modules';
import { generateEditorPath } from 'services/generators';
import { produce } from 'immer'
import { useDispatch, useSelector } from 'react-redux'
import { saveDraft, saveLastProps, saveLastModuleID } from 'redux/draftsSlice'
import PropTypes from 'prop-types'
import JsonEditor from 'modules/Placeholder/JsonEditor'

const EditorModule = ({ disableInline }) => {
  const {
    match: {
      params: {
        moduleId,
        lang,
        pageId,
        subweb
      },
    },
    history,
  } = useRouterContext();
  
  const [{ pageLiveData }, dispatch] = useEditorContext();
  const { lastProps, lastModuleId } = useSelector(state => state.drafts);
  const dispatchRedux = useDispatch()

  
  // state used to trigger an action on child editor component
  // everytime this changes the child editor knows it should handle the add
  const [addTrigger, setAddTrigger] = useState(false);
  
  // get module props from page data
  const moduleProps = useMemo(
    () => pageLiveData.find(({ name }, index) => {
      const expectedModuleId = `${name}-${index}`;
      return expectedModuleId === moduleId;
    }),
    [pageLiveData, moduleId]
  );

  // get module component name from moduleId
  const name = useMemo(() => (moduleId ? moduleId.split('-')[0] : null), [moduleId]);

  // readable name for title
  const moduleName = useMemo(() => {
    const label = (
      modules[name] && modules[name].options && modules[name].options?.label
    );
    return label || moduleId;
  }, [moduleId, name]);

  const moduleExists = useMemo(() => {
    if (modules[name]) return true;
    return false;
  }, [name]);

  const ComponentEditor = useMemo(
    () => (moduleExists ? modules[name].editor : modules.Placeholder.editor),
    [moduleExists, name, moduleProps]
  );

  const handleOnChange = newProps => {
    // find index of current module being editing
    const moduleIndex = pageLiveData.findIndex(({ name }, index) => {
      const expectedModuleId = `${name}-${index}`;
      return expectedModuleId === moduleId;
    });

    // produce used to create a new immutable page
    const newLiveData = produce(pageLiveData, draftState => {
      // eslint-disable-next-line no-param-reassign, no-nested-ternary
      draftState[moduleIndex] = JSON.stringify(newProps) === JSON.stringify(lastProps) ? lastModuleId !== moduleId ? draftState[moduleIndex] : newProps : newProps
    })

    // verify if the new data is different before dispatching to context
    const pageLiveDataJSON = JSON.stringify(pageLiveData);
    const newLiveDataJSON = JSON.stringify(newLiveData);

    if (pageLiveDataJSON !== newLiveDataJSON) {
      // update editor context
      dispatch({
        type: actions.PAGE_DATA_UPDATE,
        data: newLiveData,
      });
      dispatchRedux(saveDraft({ pageId, content: newLiveData, lang }))
      dispatchRedux(saveLastProps({ content: newProps }))
      dispatchRedux(saveLastModuleID({ moduleId }))
    } 
  };

  const options = useMemo(
    () => (
      (modules && name && modules[name])
        ? modules[name].options
        : moduleDefaults.options),
    [name]
  )

  const closeModule = () => {
    dispatch({
      type: actions.SET_MODULE_EDITED,
      data: {
        key: null,
        name: null,
      },
    });
    history.push(generateEditorPath({ lang, pageId, subweb }))
  }

  if (options.isInline && disableInline) {
    return (
      <Wrapper>
        <Top>
          {options.hasTitle && <Title modifiers={['fixMarginBottom']}>{moduleName}</Title>}
          <Button modifiers={['invisibleDarker']} onClick={closeModule}>
            <Icon icon="close" />
          </Button>
        </Top>
        <Main>
          {('description' in options && typeof options.description === 'string') && (
            <Text modifiers={['content']}>
              {options.description}
            </Text>
          )}
          <Spacer top="l" bottom="l">
            <Seperator />
          </Spacer>
          <Text modifiers={['light', 'xs']}>Edite diretamente no módulo.</Text>
          <JsonEditor
            lang={lang}
            initialProps={moduleProps}
            onChange={newProps => handleOnChange(newProps)}
            addTrigger={addTrigger}
          />
        </Main>
      </Wrapper>
    )
  }

  return (
    <Wrapper isInline={options.isInline}>
      {!options.isInline && (
        <Top>
          {options.hasTitle && <Title modifiers={['fixMarginBottom']}>{moduleName}</Title>}
          <Button modifiers={['invisibleDarker']} onClick={closeModule}>
            <Icon icon="close" />
          </Button>
        </Top>
      )}
      <Main isInline={options.isInline}>
        <ComponentEditor
          lang={lang}
          initialProps={moduleProps}
          onChange={newProps => handleOnChange(newProps)}
          addTrigger={addTrigger}
        />
        <Actions>
          {options.hasAdd && (
            <Button
              icon="add"
              modifiers={['smallText', 'dark']}
              onClick={() => setAddTrigger(!addTrigger)}
            >
            NOVO
            </Button>
          )}
        </Actions>
        {!options.isInline && (
          <JsonEditor
            lang={lang}
            initialProps={moduleProps}
            onChange={newProps => handleOnChange(newProps)}
            addTrigger={addTrigger}
          />
        )}
      </Main>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  max-height: ${props => (props.isInline ? '100%' : 'calc(100vh - 90px)')};
  overflow-y: ${props => (props.isInline ? 'initial' : 'auto')};
`

const Top = styled.div(props => css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props.theme.spacing.xl} ${props.theme.spacing.xl} ${props.theme.spacing.m} ${props.theme.spacing.xl3};
`);

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
`

const Main = styled.div(props => css`
  padding: ${props.isInline ? '0px' : `0 ${props.theme.spacing.xl} ${props.theme.spacing.xl3} ${props.theme.spacing.xl3}`};
`)

EditorModule.propTypes = {
  disableInline: PropTypes.bool,
}

EditorModule.defaultProps = {
  disableInline: false,
}

export default EditorModule;
